// entities/product/lib/productCardView.ts
//
// Чистые хелперы отображения товара в плитке каталога.
// Раньше жили внутри ProductCatalogSection.vue; вынесены, потому что теперь
// одну и ту же логику показывают пять шаблонов карточки и превью в админке.

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov'];

/** Медиа карточки: у товара это массив, у комбо/группы может не быть вовсе. */
export interface ProductCardMediaItem {
  url: string;
  key?: string;
}

/** Изображения товара для плитки. Всё, что не массив, считаем «медиа нет». */
export const getCardImages = (item: any): ProductCardMediaItem[] =>
  Array.isArray(item?.img) ? item.img : [];

/** Видео определяем по расширению: бэк не отдаёт mime для медиа карточки. */
export const isVideoUrl = (url: string): boolean =>
  typeof url === 'string' && VIDEO_EXTENSIONS.some((ext) => url.toLowerCase().endsWith(ext));

/**
 * Есть ли у товара отдельная подложка.
 *
 * От этого зависит, как показывать само блюдо: на своей подложке лежит PNG
 * с прозрачностью, и его нельзя кадрировать — обрежутся края. Без подложки
 * фото обычное, прямоугольное, и должно заполнять плитку целиком.
 * Для видео подложка смысла не имеет — оно и так занимает весь кадр.
 */
export const hasCardBackground = (item: any, mediaUrl?: string): boolean =>
  Boolean(item?.background_img?.url) && Boolean(mediaUrl) && !isVideoUrl(mediaUrl as string);

/**
 * Подложка карточки (background_img) рисуется под PNG с прозрачностью.
 */
export const getCardBackgroundStyle = (item: any, mediaUrl?: string) => {
  if (!hasCardBackground(item, mediaUrl)) return;
  return {
    backgroundImage: `url(${item.background_img.url})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };
};

/** Минимальная цена среди вариантов группы. null — вариантов нет. */
export const getMinVariantPrice = (item: any): number | null => {
  if (Array.isArray(item?.variants) && item.variants.length) {
    return Math.min(...item.variants.map((variant: any) => Number(variant?.price) || 0));
  }
  return null;
};

export interface ProductCardPrice {
  /** Число к показу. */
  value: number;
  /** Нужен ли префикс «от» — у групп и комбо цена стартовая. */
  from: boolean;
}

/**
 * Цена для плитки: комбо — final_price, группа — минимум по вариантам,
 * одиночный товар — своя цена. Один расчёт на все шаблоны, чтобы витрина
 * не начала показывать разные суммы в разных вёрстках.
 */
export const getCardPrice = (item: any): ProductCardPrice => {
  if (item?.type === 'combo') {
    return { value: Number(item?.final_price) || 0, from: true };
  }
  const minVariant = getMinVariantPrice(item);
  if (minVariant !== null) {
    return { value: minVariant, from: true };
  }
  return { value: Number(item?.price) || 0, from: false };
};

/**
 * Описание для плитки. У группы своего описания нет — берём у первого варианта,
 * иначе карточка с включённым «показывать описание» выглядела бы пустой.
 */
export const getCardDescription = (item: any): string => {
  const own = typeof item?.description === 'string' ? item.description.trim() : '';
  if (own) return own;
  const firstVariant = Array.isArray(item?.variants) ? item.variants[0] : null;
  return typeof firstVariant?.description === 'string' ? firstVariant.description.trim() : '';
};

/**
 * Текстовое поле карточки с фолбэком на варианты — тем же правилом, что и
 * {@link getCardDescription}: у группы («Шаурма» с размерами) собственных
 * состава и аллергенов нет, они лежат в вариантах. Без фолбэка страница блюда
 * не показывала бы состав НИ У ОДНОГО товара с размерами — а состав для еды
 * обязателен до заключения договора (п. 2 ст. 10 ЗоЗПП).
 *
 * Берётся первый вариант с заполненным полем, а не строго нулевой: состав
 * обычно общий на все размеры, но заполнять его могут начать с любого.
 */
const getCardTextField = (item: any, field: 'composition' | 'allergens'): string => {
  const own = typeof item?.[field] === 'string' ? item[field].trim() : '';
  if (own) return own;
  const variants = Array.isArray(item?.variants) ? item.variants : [];
  for (const variant of variants) {
    const value = typeof variant?.[field] === 'string' ? variant[field].trim() : '';
    if (value) return value;
  }
  return '';
};

/** Состав блюда для страницы/карточки — свой или первого заполненного варианта. */
export const getCardComposition = (item: any): string => getCardTextField(item, 'composition');

/** Аллергены блюда — свои или первого заполненного варианта. */
export const getCardAllergens = (item: any): string => getCardTextField(item, 'allergens');
